import { auth, db, storage } from "../firebase";

import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile
} from "firebase/auth";

import {
  getDownloadURL,
  ref,
  uploadBytes
} from "firebase/storage";

import app from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    window.clearTimeout(timeoutId);
  });
};

export const getUsersList = async () => {
  const q = query(collection(db, "users"), orderBy("email", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const createSystemUser = async ({
  email,
  password,
  name,
  hireDate,
  role,
  accessMode,
  assignedCompanies,
  canAssignCompanies,
  canMonitorBackups,
  canManageParasut,
  processProjectAccessMode,
  assignedProcessProjects
}) => {
  const functions = getFunctions(app, "us-central1");

  const createUser = httpsCallable(functions, "createSystemUser");

  const result = await createUser({
  email,
  password,
  name,
  hireDate,
  role,
  accessMode,
  assignedCompanies,
  canAssignCompanies,
  canMonitorBackups,
  canManageParasut,
  processProjectAccessMode,
  assignedProcessProjects
  });

  const createdUid = result?.data?.uid || result?.data?.user?.uid;

  if (createdUid) {
    const safeAccessMode = accessMode === "assigned" ? "assigned" : "all";
    const safeProcessProjectAccessMode =
      processProjectAccessMode === "assigned" ? "assigned" : "all";

    await updateDoc(doc(db, "users", createdUid), {
      processProjectAccessMode: safeProcessProjectAccessMode,
      assignedProcessProjects:
        safeProcessProjectAccessMode === "assigned" && Array.isArray(assignedProcessProjects)
          ? assignedProcessProjects.filter(Boolean)
          : [],
      accessMode: safeAccessMode,
      assignedCompanies:
        Array.isArray(assignedCompanies)
          ? assignedCompanies.filter(Boolean)
          : [],
      canAssignCompanies: Boolean(canAssignCompanies),
      canMonitorBackups: Boolean(canMonitorBackups),
      canManageParasut: Boolean(canManageParasut),
      updatedAt: serverTimestamp()
    });
  }

  return result.data;
};

export const updateSystemUser = async ({
  uid,
  name,
  hireDate,
  role,
  accessMode,
  assignedCompanies,
  canAssignCompanies,
  canMonitorBackups,
  canManageParasut,
  processProjectAccessMode,
  assignedProcessProjects,
  isActive
  }) => {
  if (!uid) {
    throw new Error("Kullanıcı UID bilgisi bulunamadı.");
  }

  const safeAccessMode = accessMode === "assigned" ? "assigned" : "all";
  const safeProcessProjectAccessMode =
    processProjectAccessMode === "assigned" ? "assigned" : "all";

  await updateDoc(doc(db, "users", uid), {
    name: String(name || "").trim(),
    hireDate: String(hireDate || ""),
    role,
    accessMode: safeAccessMode,
    assignedCompanies:
      Array.isArray(assignedCompanies)
        ? assignedCompanies.filter(Boolean)
        : [],
    canAssignCompanies: Boolean(canAssignCompanies),
    canMonitorBackups: role === "admin" && Boolean(canMonitorBackups),
    canManageParasut: role === "admin" && Boolean(canManageParasut),
    processProjectAccessMode: safeProcessProjectAccessMode,
    assignedProcessProjects:
      safeProcessProjectAccessMode === "assigned" && Array.isArray(assignedProcessProjects)
        ? assignedProcessProjects.filter(Boolean)
        : [],
    isActive: isActive !== false,
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const updateUserCompanyAssignments = async ({ uid, assignedCompanies }) => {
  if (!uid) {
    throw new Error("Kullanıcı UID bilgisi bulunamadı.");
  }

  await updateDoc(doc(db, "users", uid), {
    accessMode: "all",
    assignedCompanies: Array.isArray(assignedCompanies)
      ? assignedCompanies.filter(Boolean)
      : [],
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const deleteSystemUser = async (email) => {
  const functions = getFunctions(app, "us-central1");

  const deleteUser = httpsCallable(functions, "deleteUser");

  const result = await deleteUser({ email });

  return result.data;
};

export const setSystemUserPassword = async ({ uid, email, password }) => {
  const functions = getFunctions(app, "us-central1");
  const createUser = httpsCallable(functions, "createUser");

  const result = await createUser({
    userAction: "setPassword",
    uid,
    email,
    newPassword: password
  });

  return result.data;
};

export const generateSystemUserPasswordResetLink = async ({ uid, email }) => {
  const functions = getFunctions(app, "us-central1");
  const createUser = httpsCallable(functions, "createUser");

  const result = await createUser({
    userAction: "generatePasswordResetLink",
    uid,
    email
  });

  return result.data;
};

export const sendSystemUserPasswordResetEmail = async ({ email }) => {
  if (!email) {
    throw new Error("E-posta adresi bulunamadı.");
  }

  await sendPasswordResetEmail(auth, String(email).trim().toLowerCase());

  return { success: true };
};

export const updateCurrentUserProfile = async ({ uid, name, photoURL }) => {
  if (!uid) {
    throw new Error("Kullanıcı UID bilgisi bulunamadı.");
  }

  const safeName = String(name || "").trim();
  const payload = {
    name: safeName,
    updatedAt: serverTimestamp()
  };

  if (photoURL !== undefined) {
    payload.photoURL = photoURL;
  }

  await updateDoc(doc(db, "users", uid), payload);

  if (auth.currentUser) {
    await updateProfile(auth.currentUser, {
      displayName: safeName || auth.currentUser.displayName || "",
      ...(photoURL !== undefined ? { photoURL } : {})
    });
  }

  return payload;
};

export const uploadCurrentUserAvatar = async ({ uid, file }) => {
  if (!uid) {
    throw new Error("Kullanıcı UID bilgisi bulunamadı.");
  }

  if (!file) {
    throw new Error("Avatar dosyası seçilmedi.");
  }

  if (!file.type?.startsWith("image/")) {
    throw new Error("Lütfen resim formatında bir dosya seçiniz.");
  }

  if (file.size > 2 * 1024 * 1024) {
    throw new Error("Avatar görseli en fazla 2 MB olabilir.");
  }

  const extension = file.name?.split(".").pop() || "jpg";
  const filePath = `userAvatars/${uid}/${Date.now()}.${extension}`;
  const fileRef = ref(storage, filePath);

  await withTimeout(
    uploadBytes(fileRef, file),
    12000,
    "Dosya depolama hizmetine ulaşılamadı. Profil fotoğrafı daha sonra tekrar yüklenebilir."
  );

  return withTimeout(
    getDownloadURL(fileRef),
    12000,
    "Yüklenen profil fotoğrafının adresi alınamadı."
  );
};

export const changeCurrentUserPassword = async ({ currentPassword, newPassword }) => {
  const currentUser = auth.currentUser;

  if (!currentUser?.email) {
    throw new Error("Oturum bilgisi bulunamadı.");
  }

  const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
  await reauthenticateWithCredential(currentUser, credential);
  await updatePassword(currentUser, newPassword);

  return { success: true };
};
